import 'focus-visible/dist/focus-visible';

import { Auth0Provider } from '@auth0/auth0-react';
import { ChakraProvider } from '@chakra-ui/react';
import * as Sentry from '@sentry/browser';
import { QueryClientProvider } from '@tanstack/react-query';
import { ApiClientProvider } from 'api/context-provider/ApiClientProvider';
import { queryClient } from 'configs/queryClient';
import { SidebarContextProvider } from 'context/SidebarContextProvider';
import { AnimatePresence } from 'framer-motion';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import config from './config';
import { initSentry } from './configs/sentry';
import { myTheme } from './foundations/theme';
import Fonts from './global/Fonts';
import { YBugWrapper } from './global/YBugWrapper';
import { GlobalErrorBoundary } from './layout/errors';

initSentry();

if (config.posthog_api_key) {
  posthog.init(config.posthog_api_key, config.posthog_api_host_url);
}

const onError = (error: any) => {
  Sentry.captureException(error);
};

window.onerror = onError;
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason);
});

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <PostHogProvider client={posthog}>
    <StrictMode>
      <HelmetProvider>
        <YBugWrapper>
          <ChakraProvider theme={myTheme}>
            <Fonts />
            <Auth0Provider
              domain={config.authDomain.toString()}
              clientId={config.authClientId.toString()}
              audience={config.auth0Audience.toString()}
              redirectUri={`${config.baseUrl}/app/op/dashboard`}
              cacheLocation={'localstorage'}
              onRedirectCallback={(appState) => (window.location.href = appState?.returnTo || window.location?.pathname)}
            >
              <QueryClientProvider client={queryClient}>
                <ApiClientProvider>
                  <AnimatePresence exitBeforeEnter>
                    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                      <GlobalErrorBoundary>
                        <SidebarContextProvider>
                          <App />
                        </SidebarContextProvider>
                      </GlobalErrorBoundary>
                    </BrowserRouter>
                  </AnimatePresence>
                </ApiClientProvider>
              </QueryClientProvider>
            </Auth0Provider>
          </ChakraProvider>
        </YBugWrapper>
      </HelmetProvider>
    </StrictMode>
  </PostHogProvider>,
);
